import { usePage } from "@inertiajs/react"
import { BrandMotif } from "@/components/ui/brand-motif"
import { useLocalePath } from "@/lib/locale-path"
import type { GiselaTherapist, PageProps } from "@/types/inertia"

interface Props {
  therapist: GiselaTherapist
  content: { eyebrow?: string; heading?: string; lead?: string; body?: string }
}

// "About Gisela", straight under the hero: a text band — the hero already
// carries her photo, so a second picture here (the old portrait circle) only
// competed with it. Eyebrow + heading name her as the founder of Sanctuary
// Therapy Centre; lead/body are the admin-editable therapy.person copy. The
// section hides entirely until it has content.
export default function GiselaPersonBlock({ therapist: _therapist, content }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.person
  const lp = useLocalePath()
  const onGiselaHost = props.current_brand.key === "gisela"

  if (!content.lead && !content.body) return null

  return (
    <section className="relative overflow-hidden bg-stone py-16 lg:py-20">
      <BrandMotif variant="speckle" className="absolute -top-16 right-10" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <div className="lg:col-span-4">
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-ink-muted">
            {content.eyebrow || t.eyebrow}
          </p>
          <h2 className="mt-2">{content.heading || t.founder_heading}</h2>
          {/* The centre's lockup, as a credential under the heading that
              names her its founder — the way the accreditation marks sit
              further down. On her host it links to the venue's site. */}
          {onGiselaHost && (
            <a href={props.brand_urls.sanctuary} className="mt-6 block w-44">
              <img src="/logos/sanctuary-lockup.png" alt="Sanctuary Therapy Centre" className="h-auto w-full" loading="lazy" decoding="async" />
            </a>
          )}
        </div>
        <div className="lg:col-span-8">
          {content.lead && <p className="text-lg font-semibold leading-relaxed">{content.lead}</p>}
          {content.body && <p className="mt-4 whitespace-pre-line leading-relaxed text-ink-muted">{content.body}</p>}
          <div className="mt-7 flex items-center gap-5">
            <a
              href={onGiselaHost ? lp("/contact#enquiry-form") : "#enquiry-form"}
              className="text-lg font-semibold text-teal no-underline hover:underline"
            >
              {t.contact_me}
            </a>
            {onGiselaHost && (
              <>
                <span aria-hidden className="h-6 w-px bg-hairline" />
                <a href={lp("/about")} className="text-lg font-semibold no-underline hover:underline">
                  {t.more_about_me}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
